import React, { useEffect, useState } from "react";
import { BellRing, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const btn = "flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-primary transition-colors hover:border-primary disabled:opacity-50";

const ago = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return mins <= 1 ? "just now" : `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d} days ago`;
};

// "Last nudged 3 h ago" for any reminder email (teacher nudge or automatic due-date reminder).
export const NudgedAgo = ({ n, testId }) => (
  <span className={`text-[11px] ${n ? "text-primary" : "text-muted-foreground"}`} data-testid={testId} title={n ? `${n.kind === "auto" ? "Automatic reminder" : "Teacher nudge"}: ${n.title}` : undefined}>
    {n ? `Last nudged ${ago(n.at)}${n.kind === "auto" ? " (auto)" : ""}` : "Never nudged"}
  </span>
);

// Email a friendly reminder to pupils who haven't watched the chosen count-along video.
export const CountAlongNudge = ({ classId, students, videos, onSent }) => {
  const [video, setVideo] = useState(videos[0]);
  const [busy, setBusy] = useState(null);
  const todo = students.filter((s) => !(s.countalong?.watched || []).includes(video));
  const send = async (sid) => {
    setBusy(sid || "all");
    try {
      const { data } = await api.post(`/classes/${classId}/countalong-nudge`, { video, student_id: sid || null });
      if (data.sent) toast.success(`Reminder sent to ${data.sent} pupil${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} could not be emailed)` : ""}`);
      else if (data.failed) toast.error(`Couldn't email ${data.failed} pupil${data.failed === 1 ? "" : "s"}. Please try again shortly.`);
      else toast.success("Everyone has watched it already");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminder");
    } finally { setBusy(null); onSent?.(); }
  };
  return (
    <div className="rounded-xl border border-border bg-background p-4" data-testid={`countalong-nudge-${classId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold"><BellRing size={14} className="text-primary" /> Nudge pupils who haven't watched</p>
        <select value={video} onChange={(e) => setVideo(e.target.value)} data-testid={`countalong-nudge-video-${classId}`}
          className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none">
          {videos.map((v) => <option key={v} value={v}>Count along · {v}</option>)}
        </select>
      </div>
      {todo.length === 0 ? (
        <p className="mt-3 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`countalong-nudge-none-${classId}`}>Everyone has watched this one.</p>
      ) : (
        <>
          <ul className="mt-3 space-y-1.5">
            {todo.map((s) => (
              <li key={s.id} className="flex items-center justify-between text-sm" data-testid={`countalong-nudge-row-${s.id}`}>
                <span className="flex flex-col text-muted-foreground">{s.name || s.email}<NudgedAgo n={s.last_nudged} testId={`countalong-nudged-${s.id}`} /></span>
                <button type="button" onClick={() => send(s.id)} disabled={!!busy} className={btn} data-testid={`countalong-nudge-btn-${s.id}`}>
                  <BellRing size={11} /> {busy === s.id ? "Sending…" : "Nudge"}
                </button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => send(null)} disabled={!!busy} className={`${btn} mt-3`} data-testid={`countalong-nudge-all-${classId}`}>
            <BellRing size={11} /> {busy === "all" ? "Sending…" : `Nudge all ${todo.length} who haven't watched`}
          </button>
        </>
      )}
    </div>
  );
};

const MEDALS = ["#F5B301", "#C0C7D0", "#CD7F32"];

// Teacher-only weekly board: challenges finished this week (Sunday to Saturday).
export const CountAlongRace = ({ classId }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/classes/${classId}/countalong-race`).then(({ data: d }) => setData(d)).catch(() => setData({ board: [] })); }, [classId]);
  if (!data) return null;
  const board = data.board.filter((b) => b.challenges > 0);
  return (
    <div className="rounded-xl border border-border bg-background p-4" data-testid={`countalong-race-${classId}`}>
      <p className="flex items-center gap-2 text-sm font-semibold"><Trophy size={14} className="text-primary" /> Class Challenge Race · this week</p>
      <p className="text-xs text-muted-foreground">Count-along challenges finished since {data.week_start}. Only you can see this board.</p>
      {board.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground" data-testid={`countalong-race-empty-${classId}`}>No challenges finished yet this week.</p>
      ) : (
        <ol className="mt-3 space-y-1.5">
          {board.map((b, i) => (
            <li key={b.student_id} className="flex items-center justify-between text-sm" data-testid={`countalong-race-row-${b.student_id}`}>
              <span className="flex items-center gap-2">
                <span className="w-5 text-center font-mono text-xs" style={{ color: MEDALS[i] }}>{i < 3 ? <Trophy size={13} className="inline" /> : i + 1}</span>
                {b.name}
              </span>
              <span className="font-mono text-xs text-primary" data-testid={`countalong-race-count-${b.student_id}`}>{b.challenges} challenge{b.challenges === 1 ? "" : "s"}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
